/**
 * PlannerTopBar — slim sticky top bar for the Content Planner module.
 *
 *   • Page title (driven by the current route)
 *   • Subtle breadcrumb to root
 *   • Right-side utility slot (children) for page-specific actions
 *
 * Visual tokens are passed in by the layout so the bar follows the
 * same prefers-color-scheme theme as the sidebar.
 */
import React from "react";
import { useLocation } from "react-router-dom";
import { Menu } from "lucide-react";

const ROUTE_LABELS = [
  { match: (p) => p === "/portal/content-planner",                    title: "Compose",          group: "Publish" },
  { match: (p) => p.startsWith("/portal/content-planner/calendar"),   title: "Calendar",         group: "Publish" },
  { match: (p) => p.startsWith("/portal/content-calendar/plan/"),     title: "Editor",           group: "Publish" },
  { match: (p) => p.startsWith("/portal/workspace/ideas"),            title: "Ideas",            group: "Library" },
  { match: (p) => p.startsWith("/portal/content-studio/blog"),        title: "Blog studio",      group: "Library" },
  { match: (p) => p.startsWith("/portal/content-studio/docs"),        title: "Document studio",  group: "Library" },
  { match: (p) => p.startsWith("/portal/brand"),                      title: "Brand kit",        group: "Library" },
  { match: (p) => p.startsWith("/portal/workspace/trash"),            title: "Trash",            group: "System" },
];

function deriveLabels(pathname) {
  for (const r of ROUTE_LABELS) {
    if (r.match(pathname)) return { title: r.title, group: r.group };
  }
  return { title: "Planner", group: "" };
}

export default function PlannerTopBar({
  children, sidebarWidth = 260,
  bg = "rgba(250,250,247,0.85)",
  text = "#0C2A40",
  muted = "rgba(12,42,64,0.55)",
  border = "rgba(12,42,64,0.10)",
  isMobile = false,
  onMenuClick,
}) {
  const { pathname } = useLocation();
  const { title, group } = deriveLabels(pathname);

  return (
    <header
      data-testid="planner-topbar"
      style={{
        position: "sticky", top: 0, zIndex: 40,
        height: 64,
        marginLeft: sidebarWidth,
        background: bg,
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        borderBottom: `1px solid ${border}`,
        display: "flex", alignItems: "center",
        padding: "0 28px",
        gap: 12,
        transition: "margin-left 0.25s cubic-bezier(0.4,0,0.2,1)",
      }}
    >
      {isMobile && (
        <button
          type="button"
          onClick={onMenuClick}
          data-testid="planner-topbar-menu"
          aria-label="Open navigation menu"
          style={{
            background: "transparent",
            border: `1px solid ${border}`,
            color: text,
            width: 40, height: 40,
            borderRadius: 10,
            display: "inline-flex",
            alignItems: "center", justifyContent: "center",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <Menu size={20} strokeWidth={1.75} />
        </button>
      )}
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.15, minWidth: 0, flex: "0 1 auto" }}>
        {group && (
          <span
            style={{
              fontFamily: "IBM Plex Mono, ui-monospace, monospace",
              fontSize: 10, letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: muted, fontWeight: 500,
            }}
            data-testid="planner-topbar-breadcrumb"
          >
            Planner · {group}
          </span>
        )}
        <h1
          style={{
            color: text,
            fontSize: 19, fontWeight: 500,
            letterSpacing: "-0.01em",
            margin: 0, marginTop: 2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          data-testid="planner-topbar-title"
        >
          {title}
        </h1>
      </div>
      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}
           data-testid="planner-topbar-actions">
        {children}
      </div>
    </header>
  );
}
